import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FolderOpen, BookOpen, Layers, ArrowRight } from 'lucide-react';
import PageHeader from '@/components/layout/PageHeader';
import { userActivityService, type RecentActivities } from '../services/userActivityService';

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [recentActivities, setRecentActivities] = useState<RecentActivities | null>(null);

  useEffect(() => {
    const fetch = () => userActivityService.getRecentActivities().then(setRecentActivities).catch(() => {});
    fetch();
    const onVisible = () => { if (document.visibilityState === 'visible') fetch(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const hasRecent = recentActivities && (
    recentActivities.recent_projects.length > 0 ||
    recentActivities.recent_sub_projects.length > 0 ||
    recentActivities.recent_kbs.length > 0
  );

  return (
    <div className="min-h-full bg-surface-muted">
      <PageHeader
        title={t('app.title')}
        subtitle={t('app.description')}
        actions={
          <Link
            to="/projects"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            {t('home.hero.get_started')}
            <ArrowRight size={14} />
          </Link>
        }
      />

      <div className="p-6 max-w-3xl">
        {/* Quick-access row */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { href: '/projects',         icon: <FolderOpen size={18} />, label: t('home.preview.projects') },
            { href: '/knowledge-bases',  icon: <BookOpen size={18} />,   label: t('home.preview.knowledgeBases') },
          ].map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="flex items-center gap-2.5 px-4 py-3 bg-white rounded-lg text-sm font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-700 transition-colors border border-transparent hover:border-primary-100"
            >
              <span className="text-slate-400">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </div>

        {/* Recent activity */}
        <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
          {t('home.preview.recentlyAccessed')}
        </h2>

        {!hasRecent ? (
          <p className="text-sm text-slate-400 py-4">{t('home.preview.emptyHint')}</p>
        ) : (
          <div className="space-y-3">
            {recentActivities!.recent_projects.length > 0 && (
              <RecentSection
                icon={<FolderOpen size={14} />}
                label={t('home.preview.projects')}
                items={recentActivities!.recent_projects}
                onNavigate={(id) => navigate(`/project/${id}`)}
              />
            )}
            {recentActivities!.recent_sub_projects.length > 0 && (
              <RecentSection
                icon={<Layers size={14} />}
                label={t('home.preview.subProjects')}
                items={recentActivities!.recent_sub_projects}
                onNavigate={(id) => navigate(`/sub-project/${id}`)}
              />
            )}
            {recentActivities!.recent_kbs.length > 0 && (
              <RecentSection
                icon={<BookOpen size={14} />}
                label={t('home.preview.knowledgeBases')}
                items={recentActivities!.recent_kbs}
                onNavigate={(id) => navigate(`/scv2/kb/${id}`)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface RecentSectionProps {
  icon: React.ReactNode;
  label: string;
  items: { entity_id: string; entity_name: string }[];
  onNavigate: (id: string) => void;
}

function RecentSection({ icon, label, items, onNavigate }: RecentSectionProps) {
  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100">
        <span className="text-slate-400">{icon}</span>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <ul>
        {items.map((item) => (
          <li key={item.entity_id}>
            <button
              onClick={() => onNavigate(item.entity_id)}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-primary-700 transition-colors flex items-center gap-2 border-b border-slate-50 last:border-0"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
              {item.entity_name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
